'use client';

import { use } from 'react';
import Link from 'next/link';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { useAdminSessionTurns } from '../../_lib/hooks';
import { fmtSec } from '../../_lib/format';

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b'];

export default function SessionDetailPage({ params }: { params: Promise<{ sessionId: string }> }) {
  const { sessionId } = use(params);
  const { data, isFetching } = useAdminSessionTurns(sessionId);
  const loading = isFetching && !data;

  if (loading) return <div className="p-6 text-gray-500">Loading…</div>;
  if (!data || !data.summary) {
    return (
      <div className="p-6">
        <Link href="/ai-usage/sessions" className="text-sm text-blue-600 hover:underline">
          ← 返回列表
        </Link>
        <div className="mt-4 text-gray-500">session 无数据或无权访问</div>
      </div>
    );
  }

  const s = data.summary;
  // 时间序列：每 turn 的 tokens
  const tokenSeries = data.turns.map((t, i) => ({
    idx: t.turnIndex ?? i + 1,
    ts: new Date(t.ts).toLocaleTimeString(),
    tokens: t.totalTokens,
    cost: Number(t.costUsd),
  }));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Session 详情</h1>
        <Link href="/ai-usage/sessions" className="text-sm text-blue-600 hover:underline">
          ← 返回列表
        </Link>
      </div>

      <div className="bg-white rounded shadow p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Field label="员工" value={s.userLabel} />
          <Field label="项目" value={s.projectBasename || s.projectPath} />
          <Field label="工具" value={s.tool} />
          <Field label="Session ID" mono value={sessionId.slice(0, 16) + '…'} />
          <Field label="开始" value={new Date(s.startedAt).toLocaleString()} />
          <Field label="结束" value={new Date(s.endedAt).toLocaleString()} />
          <Field label="时长" value={fmtSec(s.durationSec)} />
          <Field label="Turn 数" value={s.turnCount.toString()} />
          <Field label="总 tokens" value={s.totalTokens.toLocaleString()} />
          <Field label="总成本" value={`$${Number(s.totalCostUsd).toFixed(4)}`} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded shadow p-4">
          <div className="text-sm font-medium text-gray-700 mb-3">每 Turn Token 时间线</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={tokenSeries}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="idx" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
              <Line type="monotone" dataKey="tokens" stroke="#2563eb" dot={false} strokeWidth={1.5} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded shadow p-4">
          <div className="text-sm font-medium text-gray-700 mb-3">本 Session 工具调用</div>
          {s.toolUseAgg.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-xs text-gray-400">无工具调用</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={s.toolUseAgg.map((x) => ({ name: x.name, value: x.count }))}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label={(e: any) => `${e.name}: ${e.value}`}
                >
                  {s.toolUseAgg.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-gray-500 border-b bg-gray-50">
            <tr>
              <th className="py-2 px-3 text-left">#</th>
              <th className="py-2 px-3 text-left">时间</th>
              <th className="py-2 px-3 text-left">Model</th>
              <th className="py-2 px-3 text-right">Tokens</th>
              <th className="py-2 px-3 text-right">Cost</th>
              <th className="py-2 px-3 text-left">Tools</th>
              <th className="py-2 px-3 text-left">Stop</th>
              <th className="py-2 px-3 text-left">Tier</th>
              <th className="py-2 px-3 text-left">Branch</th>
            </tr>
          </thead>
          <tbody>
            {data.turns.map((t, i) => (
              <tr key={t.id} className="border-b hover:bg-gray-50">
                <td className="py-1.5 px-3 font-mono">{t.turnIndex ?? i + 1}</td>
                <td className="py-1.5 px-3">{new Date(t.ts).toLocaleTimeString()}</td>
                <td className="py-1.5 px-3 font-mono">{t.model}</td>
                <td className="py-1.5 px-3 text-right">{t.totalTokens.toLocaleString()}</td>
                <td className="py-1.5 px-3 text-right">${Number(t.costUsd).toFixed(4)}</td>
                <td className="py-1.5 px-3">
                  {Array.isArray(t.toolNames) && t.toolNames.length > 0
                    ? t.toolNames.join(', ')
                    : '—'}
                </td>
                <td className="py-1.5 px-3 font-mono">{t.stopReason || '—'}</td>
                <td className="py-1.5 px-3 font-mono">{t.serviceTier || '—'}</td>
                <td className="py-1.5 px-3 font-mono truncate max-w-[160px]" title={t.gitBranch || ''}>
                  {t.gitBranch || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    </div>
  );
}

